import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import UpgradeBanner from "@/components/UpgradeBanner";
import { Activity, Users, AlertTriangle, TrendingUp, ChevronRight } from "lucide-react";
import WorkloadSnapshot from "@/components/WorkloadSnapshot";
import HotPitcherTile from "@/components/HotPitcherTile";
import WarmupComplianceCard from "@/pages/coach/WarmupComplianceCard";
import TeamBodyScanHeatmap from "@/components/TeamBodyScanHeatmap";
import RescreenWatchlistCard from "@/components/RescreenWatchlistCard";
import DepartmentVerdictBoard from "@/components/DepartmentVerdictBoard";
import TierContextHeader from "@/components/TierContextHeader";
import HumeAlertsPanel from "@/components/HumeAlertsPanel";

export default function CoachDashboard() {
  const [summary, setSummary] = useState(null);
  const [roster, setRoster] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([api.get("/dashboard/summary"), api.get("/players")]);
      setSummary(s.data);
      setRoster(r.data);
      try {
        const { data } = await api.get("/billing/tier");
        setTierInfo(data);
      } catch { /* tier banner is optional */ }
    })();
  }, []);

  const KPI = ({ label, value, sub, Icon, color }) => (
    <div className="rounded-xl ink-card p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{label}</div>
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}26`, color }}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-4 font-display font-black text-5xl tracking-tighter font-mono-num text-white" data-testid={`kpi-${label.toLowerCase()}`}>
        {value ?? "—"}
      </div>
      <div className="text-xs text-white/50 mt-1">{sub}</div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="coach-dashboard-page">
      <UpgradeBanner />
      <TierContextHeader tierInfo={tierInfo} />
      <div>
        <p className="text-sm text-white/55 max-w-2xl">
          Movement · Performance · Workload · Recovery — connected into one intelligence layer that surfaces the coaching decision before you open the app.
        </p>
      </div>

      {/* DEPARTMENT VERDICT — the daily staff meeting */}
      <DepartmentVerdictBoard />

      {/* Hume × Workload recovery alerts */}
      <HumeAlertsPanel />

      {/* HERO Readiness KPI */}
      {summary?.avg_readiness != null && (() => {
        const s = summary.avg_readiness;
        const band = s >= 80 ? ["peak", "#16A34A"] : s >= 65 ? ["good", "#22D3EE"] : s >= 45 ? ["caution", "#F59E0B"] : ["risk", "#EF4444"];
        return (
          <div className="rounded-2xl ink-card-elevated p-6 relative overflow-hidden" data-testid="readiness-hero">
            <div className="absolute top-0 right-0 bottom-0 w-1.5" style={{ backgroundColor: band[1] }} />
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div>
                <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Team Readiness</div>
                <div className="font-display font-black text-7xl md:text-8xl tracking-tighter font-mono-num leading-none" style={{ color: band[1] }} data-testid="team-readiness-score">
                  {Math.round(s)}
                </div>
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full uppercase tracking-[0.2em] text-[10px] font-bold" style={{ backgroundColor: `${band[1]}22`, color: band[1] }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: band[1] }} />
                  {band[0]} · ready today?
                </div>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] tracking-[0.2em] font-bold text-white/40 uppercase">Lowest Athlete</div>
                  <div className="font-display font-black text-3xl font-mono-num text-white mt-1">{summary.lowest_readiness != null ? Math.round(summary.lowest_readiness) : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] font-bold text-white/40 uppercase">7-Day Workload + Watch Min</div>
                  <div className="font-display font-black text-3xl font-mono-num text-white mt-1">{(summary.total_workload_7d ?? 0) + (summary.total_workout_minutes_7d ?? 0)}</div>
                </div>
                <div className="col-span-2 text-xs text-white/50 mt-1">
                  Blended HRV-vs-baseline · sleep debt · acute:chronic load · subjective readiness · all in one number.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Total" value={summary?.total_players} sub="active athletes" Icon={Users} color="#3B82F6" />
        <KPI label="Ready" value={summary?.ready} sub="green light" Icon={Activity} color="#16A34A" />
        <KPI label="Hold" value={summary?.hold} sub="watch list" Icon={TrendingUp} color="#F59E0B" />
        <KPI label="Critical" value={summary?.critical} sub="shutdown" Icon={AlertTriangle} color="#EF4444" />
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-display font-bold text-xl text-white">Roster Health</h2>
            <Link to="/dashboard/roster" className="text-xs font-semibold uppercase tracking-[0.2em] flex items-center gap-1" style={{ color: "var(--brand-secondary)" }}>
              Manage <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="rounded-xl ink-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--ink-border)] text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Athlete</th>
                  <th className="text-left px-4 py-3">Position</th>
                  <th className="text-right px-4 py-3">Ready</th>
                  <th className="text-right px-4 py-3">7-Day Vol</th>
                  <th className="text-right px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ink-border)]">
                {roster.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-white/40 text-sm">No players yet.</td></tr>
                )}
                {roster.map((p) => (
                  <tr key={p.user_id} className="hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <Link to={`/dashboard/player/${p.user_id}`} className="flex items-center gap-3" data-testid={`roster-link-${p.user_id}`}>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-white text-xs ring-1 ring-white/10" style={{ backgroundColor: "var(--brand-primary)" }}>
                          {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                        </div>
                        <div>
                          <div className="font-semibold text-white">{p.name}</div>
                          <div className="text-xs text-white/40">{p.profile?.year || "—"} · {p.profile?.dominant_arm || "?"}HP</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-white/60">{p.profile?.position || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {p.readiness?.score != null && (() => {
                        const s = p.readiness.score;
                        const c = s >= 80 ? "#16A34A" : s >= 65 ? "#22D3EE" : s >= 45 ? "#F59E0B" : "#EF4444";
                        return <span className="font-mono-num font-bold" style={{ color: c }} data-testid={`readiness-${p.user_id}`}>{Math.round(s)}</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold text-white">{p.workload_7d}</td>
                    <td className="px-4 py-3 text-right"><StatusBadge status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="font-display font-bold text-xl mb-3 text-white">Today's Pulse</h2>
          <div className="space-y-6">
            <HotPitcherTile />
            <WarmupComplianceCard />
            <TeamBodyScanHeatmap days={14} />
            <RescreenWatchlistCard />
            <WorkloadSnapshot />
            <div className="rounded-xl ink-card p-5">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Total team workload (7d)</div>
            <div className="font-display font-black text-6xl tracking-tighter mt-2 font-mono-num text-white">
              {summary?.total_workload_7d ?? 0}
            </div>
            <div className="text-xs text-white/50 mt-1">total throws / pitches</div>
            <hr className="my-5 border-[var(--ink-border)]" />
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Apple Watch / Wear OS</div>
            <div className="font-display font-black text-3xl tracking-tighter mt-2 font-mono-num text-white">
              {summary?.total_workout_minutes_7d ?? 0}<span className="text-base text-white/40 ml-1">min</span>
            </div>
            <div className="text-xs text-white/50 mt-1">total practice minutes (7d)</div>
            <hr className="my-5 border-[var(--ink-border)]" />
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-white/60">Ready</span>
                <span className="font-mono-num font-bold status-ready">{summary?.ready ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60">Hold</span>
                <span className="font-mono-num font-bold status-hold">{summary?.hold ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60">Critical</span>
                <span className="font-mono-num font-bold status-critical">{summary?.critical ?? 0}</span>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}
